"use client";

import Image from "next/image";
import React, { useEffect, useState, useRef } from "react";
import vLogo from "../../components/images/vilva-new-logo.webp";
import Select from "react-select";
import toast from "react-hot-toast";
import Link from "next/link";
import { PartnerRightSec } from "../PartnerRightSec";
import { getFlagUrl } from "@/lib/imageUrl";
import { getApiUrl } from "@/lib/apiUrl";
import { stripDangerousChars } from "@/lib/validation";
import { trackEvent } from "@/lib/metaPixel";
import OtpChannelNotice from "@/components/OtpChannelNotice";

// Define interfaces
interface Country {
  id: number;
  country: string;
  short_code: string;
  calling_code: number;
  phone_length: number;
  status: number;
  flag: string;
  currency: string;
  currency_symbol: string;
}

interface CustomOption {
  value: string;
  label: string;
  flag: string;
  calling_code: string;
  country_name: string;
  phone_length: number;
}

interface VisitorData {
  city: string;
  region: string;
  country: string;
  postal: string;
  latitude: string;
  longitude: string;
  org: string;
}

interface PartnerType {
  id: number;
  name: string;
}

type FormErrors = {
  name?: string;
  businessEmail?: string;
  mobile?: string;
  country?: string;
  companyName?: string;
  description?: string;
  captcha?: string;
  otp?: string;
  category?: string;
  companyWebsite?: string;
};

export const PartnersForm = () => {
  const [formData,  setFormData] = useState({
    name: "",
    businessEmail: "",
    country: "",
    countryCode: "",
    mobile: "",
    companyName: "",
    companyWebsite: "",
    description: "",
    partnerTypeId: "",
    "g-recaptcha-response": "",
  });
  const [otpData, setOtpData] = useState(["", "", "", ""]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [formLoading, setFormLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [partnerTypes, setPartnerTypes] = useState<PartnerType[]>([]);
  const [showOtpSection, setShowOtpSection] = useState(false);
  // Which channel the OTP actually went out on (WhatsApp -> SMS -> email),
  // and the masked destination — both decided by the backend at send time.
  const [otpChannel, setOtpChannel] = useState<string | null>(null);
  const [otpSentTo, setOtpSentTo] = useState<string | null>(null);
  const [resendTimer, setResendTimer] = useState(120);
  const [canResendOtp, setCanResendOtp] = useState(false);
  const [visitorData, setVisitorData] = useState<VisitorData>({
    city: "Unknown",
    region: "Unknown",
    country: "Unknown",
    postal: "Unknown",
    latitude: "Unknown",
    longitude: "Unknown",
    org: "Unknown",
  });
  const [isCountryLoading, setIsCountryLoading] = useState(false);
  const [location, setLocation] = useState("INDIA");
  const buttonRef = useRef<HTMLButtonElement>(null);
  const otpInputRef = useRef<HTMLInputElement>(null);

  const recaptchaSiteKey = "6Lf9iEYqAAAAAGBkHMfKHyrXoDP3tyX_yZ8cL9b-";
  const assetscountries = getFlagUrl();
  const key = "waSs57FdgthqfzVkv3RMrpTjBcYGKJJRQWtNxhmbS2EPpUjwy8";

  // Location animation
  useEffect(() => {
    const interval = setInterval(() => {
      setLocation((prev) => (prev === "INDIA" ? "TAMILNADU" : "INDIA"));
    }, 2000);
    return () => clearInterval(interval);
  }, []);


  // Fetch countries
  useEffect(() => {
    setIsCountryLoading(true);

    fetch("/api/countries")
      .then((response) => response.json())
      .then((data: Country[]) => {
        setCountries(Array.isArray(data) ? data : []);

        const india = data.find(
          (country) =>
            country.calling_code === 91 || country.short_code === "IN"
        );

        if (india) {
          setFormData((prev) => ({
            ...prev,
            country: india.id.toString(),
            countryCode: `+${india.calling_code}`,
          }));
        }

        setIsCountryLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching countries:", error);
        setIsCountryLoading(false);
      });
  }, []);

  // Fetch partner types
  useEffect(() => {
    fetch(getApiUrl("/v1/partner-types"), {
      method: "GET",
      redirect: "follow",
    })
      .then((response) => response.json())
      .then((result) => {
        if (result.status && Array.isArray(result.data)) {
          setPartnerTypes(result.data);
        } else {
          console.error("Invalid partner types response:", result);
        }
      })
      .catch((error) => {
        console.error("Error fetching partner types:", error);
      });
  }, []);

  // Set default country based on IP
  useEffect(() => {
    if (countries.length > 0) {
      // Server-side lookup (no CORS/rate-limit exposure in the browser — see app/api/geo).
      fetch('/api/geo')
        .then((res) => res.json())
        .then((data) => {
          setVisitorData({
            city: data.city || "Unknown",
            region: data.region || "Unknown",
            country: data.country_code || "Unknown",
            postal: data.postal || "Unknown",
            latitude: data.latitude?.toString() || "Unknown",
            longitude: data.longitude?.toString() || "Unknown",
            org: data.org || "Unknown",
          });

          const matchingCountry = countries.find(
            (country) => country.short_code === data.country_code
          );

          if (matchingCountry) {
            setFormData((prev) => ({
              ...prev,
              country: matchingCountry.id.toString(),
              countryCode: `+${matchingCountry.calling_code}`,
            }));
          }
        })
        .catch((error) => {
          console.error("Error fetching visitor data:", error);
        });
    }
  }, [countries]);

  // Resend OTP timer
  useEffect(() => {
    if (showOtpSection && resendTimer > 0) {
      const timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResendOtp(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [showOtpSection, resendTimer]);

  // Focus first OTP input when OTP section appears
  useEffect(() => {
    if (showOtpSection && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [showOtpSection]);

  // Encryption function
  const encryptData = async (plainText: string, encryptKey: string) => {
    const encoder = new TextEncoder();
    try {
      const encodedKey = encoder.encode(encryptKey);
      const cryptoKey = await crypto.subtle.importKey(
        "raw",
        await crypto.subtle.digest("SHA-256", encodedKey),
        { name: "AES-GCM" },
        false,
        ["encrypt"]
      );
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const ciphertext = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv, tagLength: 128 },
        cryptoKey,
        encoder.encode(plainText)
      );
      const encryptedData = new Uint8Array([...iv, ...new Uint8Array(ciphertext)]);
      return btoa(String.fromCharCode(...encryptedData));
    } catch (err) {
      console.error("Encryption failed:", err);
      throw new Error("Encryption failed");
    }
  };

  // Map countries to custom options for react-select
  const customOptions: CustomOption[] = countries.map((country) => ({
    value: country.id.toString(),
    label: `+${country.calling_code}`,
    flag: country.flag ? `${assetscountries}${country.flag}` : "",
    calling_code: `+${country.calling_code}`,
    country_name: country.country,
    phone_length: country.phone_length,
  }));

  // Format the dropdown option to show flag, calling code, and country name
  const formatOptionLabel = ({ flag, calling_code, country_name }: CustomOption) => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      {flag && <img src={flag} alt={`${country_name} flag`} style={{ width: "20px" }} />}
      <span>{calling_code}</span>
    </div>
  );

  const handleCountryChange = (selectedOption: CustomOption | null) => {
    if (selectedOption) {
      setFormData((prev) => ({
        ...prev,
        country: selectedOption.value,
        countryCode: selectedOption.calling_code,
        mobile: "",
      }));
      setErrors((prev) => ({ ...prev, country: "" }));
    } else {
      setFormData((prev) => ({
        ...prev,
        country: "",
        countryCode: "",
      }));
    }
  };

  const countryCodeStyles = {
    control: (provided: any) => ({
      ...provided,
      borderRadius: "8px 0 0 8px",
      fontFamily: "Latinka Regular",
      fontSize: "16px",
      color: "#090D28",
      minWidth: "100px",
      height: "42px",
      display: "flex",
      alignItems: "center",
      border: "1px solid #D1D7DD",
      borderRight: "none",
      backgroundColor: "white",
      cursor: "pointer",
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "#090D28",
      fontFamily: "Latinka Regular",
      fontSize: "16px",
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      display: "none",
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      display: "none",
    }),
    menuList: (provided: any) => ({
      ...provided,
      maxHeight: "300px",
      "&::-webkit-scrollbar": {
        width: "6px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#195AFF",
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-track": {
        background: "#E6E9FF",
        borderRadius: "3px",
      },
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: "#090D28",
      fontFamily: "Latinka Regular",
      fontSize: "16px",
      display: "flex",
      alignItems: "center",
      height: "100%",
    }),
    input: (provided: any) => ({
      ...provided,
      caretColor: "transparent",
    }),
  };

  // Handle form input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    let clean: string;
    if (/email/i.test(name)) {
      clean = value;
    } else if (name === 'mobile') {
      const maxLen = countries.find((c) => c.id.toString() === formData.country)?.phone_length || 10;
      clean = value.replace(/\D/g, '').slice(0, maxLen);
    } else {
      clean = stripDangerousChars(value);
    }
    setFormData((prev) => ({ ...prev, [name]: clean }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Handle OTP input changes
  const handleOtpChange = (index: number, value: string) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otpData];
    newOtp[index] = value;
    setOtpData(newOtp);

    if (value && index < otpData.length - 1) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace") {
      if (otpData[index] === "" && index > 0) {
        const prevInput = document.getElementById(`otp-${index - 1}`);
        prevInput?.focus();
      }
    }
  };

  // Handle reCAPTCHA change
  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
    setFormData((prev) => ({ ...prev, "g-recaptcha-response": token || "" }));
    setErrors((prev) => ({ ...prev, captcha: "" }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors: FormErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const urlRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/;

    if (!formData.name.trim()) newErrors.name = "Full name is required";
    if (!formData.businessEmail.trim()) newErrors.businessEmail = "Business email is required";
    else if (!emailRegex.test(formData.businessEmail)) newErrors.businessEmail = "Please enter a valid email address";
    if (!formData.country) newErrors.country = "Country code is required";
    if (!formData.mobile.trim()) newErrors.mobile = "Mobile number is required";
    else if (
      countries.find((c) => c.id.toString() === formData.country)?.phone_length &&
      formData.mobile.length !==
      countries.find((c) => c.id.toString() === formData.country)?.phone_length
    ) {
      newErrors.mobile = `Mobile number must be ${
        countries.find((c) => c.id.toString() === formData.country)?.phone_length
      } digits`;
    }
    if (!formData.companyName.trim()) newErrors.companyName = "Company name is required";
    if (!formData.description.trim()) newErrors.description = "Description is required";
    if (!formData.partnerTypeId) newErrors.category = "Partnership type is required";
    // if (!recaptchaToken) newErrors.captcha = "Please complete the reCAPTCHA";
    if (formData.companyWebsite && !urlRegex.test(formData.companyWebsite)) {
      newErrors.companyWebsite = "Please enter a valid URL";
    }
    return newErrors;
  };

  // Validate OTP
  const validateOtp = () => {
    const otp = otpData.join("");
    if (otp.length !== 4 || !/^\d{4}$/.test(otp)) {
      return "Please enter a valid 4-digit OTP";
    }
    return "";
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setFormLoading(true);

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    // Include country code in mobile number (remove + sign)
    const mobileWithCountryCode = `${formData.countryCode.replace("+", "")}${formData.mobile}`;

    const payload = {
      business_email: formData.businessEmail,
      mobile: mobileWithCountryCode,
      company_name: formData.companyName,
      partner_type_id: parseInt(formData.partnerTypeId, 10),
      company_website: formData.companyWebsite || "",
      others: [formData.description],
      recaptcha_token: formData["g-recaptcha-response"],
    };

    try {
      const response = await fetch(getApiUrl("/v1/partner-request"), {
        method: "POST",
        headers: myHeaders,
        body: JSON.stringify(payload),
        redirect: "follow",
      });

      const result = await response.json();
      if (response.ok && result.status) {
        trackEvent(
          'Lead',
          { content_name: 'Partner request', content_category: 'partner' },
          { em: formData.businessEmail },
        );
        setShowOtpSection(true);
        // The backend tries WhatsApp, then SMS, then email and reports which
        // one actually took — show that instead of a generic guess.
        setOtpChannel(result.channel ?? null);
        setOtpSentTo(result.sent_to ?? null);
        notify(result.message || "OTP sent to your Email/WhatsApp");
      } else {
        if (result.errors) {
          const apiErrors: FormErrors = {};
          if (result.errors.business_email) apiErrors.businessEmail = result.errors.business_email[0];
          if (result.errors.company_website) apiErrors.companyWebsite = result.errors.company_website[0];
          if (result.errors.recaptcha_token) apiErrors.captcha = result.errors.recaptcha_token[0];
          setErrors(apiErrors);
          notifyerror("Please correct the errors in the form");
        } else {
          notifyerror(result.message || "Failed to submit request");
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      notifyerror("An error occurred. Please try again.");
    } finally {
      setFormLoading(false);
    }
  };

  // Handle OTP submission
  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const otpError = validateOtp();
    if (otpError) {
      setErrors({ otp: otpError });
      return;
    }

    setFormLoading(true);

    const otp = otpData.join("");

    // Include country code in mobile number (remove + sign)
    const mobileWithCountryCode = `${formData.countryCode.replace("+", "")}${formData.mobile}`;

    try {
      // First API call - Partner OTP verification
      const response = await fetch(
        getApiUrl("/v1/partner-request/verify-otp"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            mobile: mobileWithCountryCode,
            otp: otp,
          }),
        }
      );

      const result = await response.json();

      if (response.ok && result.status) {
        notify("OTP Verified Successfully");

        // Second API call - Registration API (screen 1)
        try {
          const registrationPayload = {
            screen: 1,
            name: formData.name,
            business_name: formData.companyName,
            email: formData.businessEmail,
            mobile: mobileWithCountryCode,
            country_id: formData.country,
          };

          const encryptedData = await encryptData(JSON.stringify(registrationPayload), key);
          const regPayload = {
            "g-recaptcha-response": recaptchaToken,
            payload: encryptedData,
          };

          const regResponse = await fetch(
            getApiUrl("/customer/v1/register"),
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(regPayload),
            }
          );

          const regResult = await regResponse.json();

          if (regResult.code === 200) {
            notify("Registration initiated successfully!");
            // Redirect with partnerregister query parameter and mobile number
            window.location.href = `/signin-with-otp?partnerregister=true&mobile=${formData.mobile}`;
          } else {
            // If registration fails, still redirect but show warning
            console.error("Registration API Error:", regResult.message);
            // Redirect anyway since partner OTP was verified
            window.location.href = `/signin-with-otp?partnerregister=true&mobile=${formData.mobile}`;
          }
        } catch (regError) {
          console.error("Registration API Error:", regError);
          // If registration API fails, still redirect since partner OTP was verified
          window.location.href = `/signin-with-otp?partnerregister=true&mobile=${formData.mobile}`;
        }
      } else {
        setErrors({ otp: result.message || "Invalid OTP" });
        notifyerror(result.message || "Invalid OTP");
      }
    } catch (error) {
      console.error("OTP Verify Error:", error);
      notifyerror("Something went wrong");
    } finally {
      setFormLoading(false);
    }
  };

  // Handle resend OTP
  const handleResendOtp = async () => {
    if (canResendOtp) {
      setFormLoading(true);
      setResendTimer(120);
      setCanResendOtp(false);
      setOtpData(["", "", "", ""]);

      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      // Include country code in mobile number (remove + sign)
      const mobileWithCountryCode = `${formData.countryCode.replace("+", "")}${formData.mobile}`;

      const payload = {
        business_email: formData.businessEmail,
        mobile: mobileWithCountryCode,
        company_name: formData.companyName,
        partner_type_id: parseInt(formData.partnerTypeId, 10),
        company_website: formData.companyWebsite || "",
        others: [formData.description],
        recaptcha_token: formData["g-recaptcha-response"],
      };

      try {
        const response = await fetch(getApiUrl("/v1/partner-request"), {
          method: "POST",
          headers: myHeaders,
          body: JSON.stringify(payload),
          redirect: "follow",
        });

        const result = await response.json();
        if (response.ok && result.status) {
          setOtpChannel(result.channel ?? null);
          setOtpSentTo(result.sent_to ?? null);
          notify(result.message || "OTP resent to your Email/WhatsApp");
          if (otpInputRef.current) {
            otpInputRef.current.focus();
          }
        } else {
          notifyerror(result.message || "Failed to resend OTP");
        }
      } catch (error) {
        console.error("Error resending OTP:", error);
        notifyerror("An error occurred. Please try again.");
      } finally {
        setFormLoading(false);
      }
    }
  };

  const notify = (msg: string) =>
    toast.success(msg, {
      style: {
        border: "1px solid #00b852",
        padding: "16px",
        color: "#00b852",
      },
      iconTheme: {
        primary: "#00b852",
        secondary: "#FFFAEE",
      },
    });

  const notifyerror = (msg: string) =>
    toast.error(msg, {
      style: {
        border: "1px solid #cc0000",
        padding: "16px",
        color: "#cc0000",
      },
      iconTheme: {
        primary: "#cc0000",
        secondary: "#FFFAEE",
      },
    });

  // Check if submit button should be disabled
  const isSubmitDisabled =
    !formData.name ||
    !formData.businessEmail ||
    !formData.country ||
    !formData.mobile ||
    !formData.companyName ||
    !formData.description ||
    !formData.partnerTypeId ||
    !recaptchaToken ||
    formLoading;

  return (
    <React.Fragment>
      <div className="min-h-screen desktop-bg">
        {/* Header */}
        <div className="flex sticky top-0 justify-center bg-white py-[6px] z-50">
          <Link href="/">
            <Image src={vLogo} alt="Vilva Business Logo" className="w-[130px]" />
          </Link>
        </div>

        {/* Main Content */}
        <div className="flex justify-center min-h-[90vh] pb-[40px] items-start md:items-center md:px-4 lg:px-0">
          <div className="w-full max-w-[918px]">
            {/* Title */}
            <h1 className="text-[#090D28] font-Schibsted_Grotesk text-[26px] md:text-[42px] font-normal leading-[120%] text-center mb-6">
              Become a{" "}
              <span className="bg-gradient-to-r from-[#21D018] to-[#05B4FF] bg-clip-text text-transparent font-Schibsted_Grotesk text-[26px] md:text-[42px] font-normal leading-[120%]">
                Vilva Partner
              </span>
            </h1>

            {/* Card Container */}
            <div className="grid md:grid-cols-2 gap-0 bg-white md:rounded-2xl md:shadow-[0px_292px_82px_rgba(6,58,140,0),0px_187px_75px_rgba(6,58,140,0.01),0px_105px_63px_rgba(6,58,140,0.05),0px_47px_47px_rgba(6,58,140,0.09),0px_12px_26px_rgba(6,58,140,0.10)] overflow-hidden">
              {/* Left Side - Form */}
              <div className="p-6 lg:p-[32px] md:pb-[24px]">
                <div className="max-w-md mx-auto flex flex-col md:h-full h-[80vh] overflow-y-scroll scrollbar-hide justify-between">
                  {!showOtpSection ? (
                    <form onSubmit={handleSubmit} className="space-y-[16px] px-1">
                      <div className="text-center mb-6">
                        <h2 className="text-[#002F81] text-center text-[24px] font-normal leading-[100%] font-NeueMontreal mb-2">
                          Let&apos;s Build Something Great Together!
                        </h2>
                      </div>

                      {/* Full Name */}
                      <div>
                        <label
                          htmlFor="name"
                          className="text-[#090D28] text-[14px] font-[500] leading-[100%] schibsted-grotesk mb-[5px]"
                        >
                          Full name <span style={{ color: "#EE3535" }}>*</span>
                        </label>
                        <div className="relative">
                          <input
                            className={`w-full h-[42px] px-[16px] py-2.5 rounded-[8px] border border-[#D1D7DD] bg-white text-[#090D28] schibsted-grotesk text-base font-normal leading-[120%] outline-none transition-colors duration-200 placeholder:text-[#6C6F87] placeholder:schibsted-grotesk placeholder:text-base placeholder:font-normal placeholder:leading-[100%] focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/10 ${errors.name ? "border-[#EE3535] focus:border-[#EE3535] focus:ring-[#EF4444]/10" : ""}`}
                            type="text"
                            name="name"
                            id="name"
                            placeholder="Enter Name"
                            value={formData.name}
                            onChange={handleChange}
                          />
                        </div>
                        {errors.name && (
                          <p className="flex items-center text-[#EE3535] text-[14px] mt-[4px] schibsted-grotesk">
                            {errors.name}
                          </p>
                        )}
                      </div>

                      {/* Business Email */}
                      <div>
                        <label
                          htmlFor="businessEmail"
                          className="text-[#090D28] text-[14px] font-[500] leading-[100%] schibsted-grotesk mb-[5px]"
                        >
                          Business email <span style={{ color: "#EE3535" }}>*</span>
                        </label>
                        <div className="relative">
                          <input
                            className={`w-full h-[42px] px-[16px] py-2.5 rounded-[8px] border border-[#D1D7DD] bg-white text-[#090D28] schibsted-grotesk text-base font-normal leading-[120%] outline-none transition-colors duration-200 placeholder:text-[#6C6F87] placeholder:schibsted-grotesk placeholder:text-base placeholder:font-normal placeholder:leading-[100%] focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/10 ${errors.businessEmail ? "border-[#EE3535] focus:border-[#EE3535] focus:ring-[#EF4444]/10" : ""}`}
                            type="email"
                            name="businessEmail"
                            id="businessEmail"
                            placeholder="Enter Business Email"
                            value={formData.businessEmail}
                            onChange={handleChange}
                          />
                        </div>
                        {errors.businessEmail && (
                          <p className="flex items-center text-[#EE3535] text-[14px] mt-[4px] schibsted-grotesk">
                            {errors.businessEmail}
                          </p>
                        )}
                      </div>

                      {/* Mobile Number */}
                      <div>
                        <label
                          htmlFor="mobile"
                          className="text-[#090D28] text-[14px] font-[500] leading-[100%] schibsted-grotesk mb-[5px]"
                        >
                          Mobile Number <span style={{ color: "#EE3535" }}>*</span>
                        </label>
                        <div className="relative flex items-center">
                          <div className="relative">
                            <Select
                              name="country"
                                instanceId="country-code-select"
                              options={customOptions}
                              value={customOptions.find((option) => option.value === formData.country) || null}
                              onChange={handleCountryChange}
                              formatOptionLabel={formatOptionLabel}
                              styles={countryCodeStyles}
                              placeholder="+91"
                              isDisabled={isCountryLoading}
                              isLoading={isCountryLoading}
                              isSearchable={false}
                              aria-label="Select country code"
                            />
                            <svg
                              className="absolute top-1/2 right-1 transform -translate-y-1/2 pointer-events-none"
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <path
                                d="M4 6L8 10L12 6"
                                stroke="black"
                                strokeWidth="1.25"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <input
                            className={`w-full h-[42px] px-[16px] py-2.5 rounded-r-[8px] border border-[#D1D7DD] bg-white text-[#090D28] schibsted-grotesk text-base font-normal leading-[120%] outline-none transition-colors duration-200 placeholder:text-[#6C6F87] placeholder:schibsted-grotesk placeholder:text-base placeholder:font-normal placeholder:leading-[100%] focus:border-[#2563eb] focus:border focus:border-l-1 focus:ring-2 focus:ring-[#2563eb]/10 ${errors.mobile || errors.country ? "border border-l-1 border-[#EE3535] focus:border-[#EE3535] focus:ring-[#EF4444]/10" : "border-l-0 focus:border-l-[#2563eb]"}`}
                            type="text"
                            name="mobile"
                            id="mobile"
                            placeholder="Enter mobile number"
                            inputMode="numeric"
                            maxLength={countries.find((c) => c.id.toString() === formData.country)?.phone_length || 10}
                            value={formData.mobile}
                            onChange={handleChange}
                            disabled={isCountryLoading}
                          />
                        </div>
                        {(errors.mobile || errors.country) && (
                          <p className="flex items-center text-[#EE3535] text-[14px] mt-[4px] schibsted-grotesk">
                            {errors.mobile || errors.country}
                          </p>
                        )}
                      </div>

                      {/* Company Name */}
                      <div>
                        <label
                          htmlFor="companyName"
                          className="text-[#090D28] text-[14px] font-[500] leading-[100%] schibsted-grotesk mb-[5px]"
                        >
                          Company / Organization name <span style={{ color: "#EE3535" }}>*</span>
                        </label>
                        <div className="relative">
                          <input
                            className={`w-full h-[42px] px-[16px] py-2.5 rounded-[8px] border border-[#D1D7DD] bg-white text-[#090D28] schibsted-grotesk text-base font-normal leading-[120%] outline-none transition-colors duration-200 placeholder:text-[#6C6F87] placeholder:schibsted-grotesk placeholder:text-base placeholder:font-normal placeholder:leading-[100%] focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/10 ${errors.companyName ? "border-[#EE3535] focus:border-[#EE3535] focus:ring-[#EF4444]/10" : ""}`}
                            type="text"
                            name="companyName"
                            id="companyName"
                            placeholder="Enter Company Name"
                            value={formData.companyName}
                            onChange={handleChange}
                          />
                        </div>
                        {errors.companyName && (
                          <p className="flex items-center text-[#EE3535] text-[14px] mt-[4px] schibsted-grotesk">
                            {errors.companyName}
                          </p>
                        )}
                      </div>

                      {/* Partnership Type */}
                      <div>
                        <label
                          htmlFor="category"
                          className="text-[#090D28] text-[14px] font-[500] leading-[100%] schibsted-grotesk mb-[5px]"
                        >
                          Partnership type <span style={{ color: "#EE3535" }}>*</span>
                        </label>
                        <div className="relative">
                          <select
                            className={`w-full h-[42px] px-[16px] py-2.5 rounded-[8px] border border-[#D1D7DD] bg-white text-[#090D28] schibsted-grotesk text-base font-normal leading-[120%] appearance-none outline-none transition-colors duration-200 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/10 ${errors.category ? "border-[#EE3535] focus:border-[#EE3535] focus:ring-[#EF4444]/10" : ""}`}
                            name="partnerTypeId"
                            id="category"
                            value={formData.partnerTypeId}
                            onChange={handleChange}
                          >
                            <option value="" disabled>
                              Select Type
                            </option>
                            {partnerTypes.map((type) => (
                              <option key={type.id} value={type.id}>
                                {type.name}
                              </option>
                            ))}
                          </select>
                          <svg
                            className="pointer-events-none absolute right-4 top-1/2 transform -translate-y-1/2"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M4 6L8 10L12 6"
                              stroke="black"
                              strokeWidth="1.25"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        {errors.category && (
                          <p className="flex items-center text-[#EE3535] text-[14px] mt-[4px] schibsted-grotesk">
                            {errors.category}
                          </p>
                        )}
                      </div>

                      {/* Company Website */}
                      <div>
                        <label
                          htmlFor="companyWebsite"
                          className="text-[#090D28] text-[14px] font-[500] leading-[100%] schibsted-grotesk mb-[5px]"
                        >
                          Company website (Optional)
                        </label>
                        <div className="relative">
                          <input
                            className={`w-full h-[42px] px-[16px] py-2.5 rounded-[8px] border border-[#D1D7DD] bg-white text-[#090D28] schibsted-grotesk text-base font-normal leading-[120%] outline-none transition-colors duration-200 placeholder:text-[#6C6F87] placeholder:schibsted-grotesk placeholder:text-base placeholder:font-normal placeholder:leading-[100%] focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/10 ${errors.companyWebsite ? "border-[#EE3535] focus:border-[#EE3535] focus:ring-[#EF4444]/10" : ""}`}
                            type="text"
                            name="companyWebsite"
                            id="companyWebsite"
                            placeholder="Website URL (e.g., https://example.com)"
                            value={formData.companyWebsite}
                            onChange={handleChange}
                          />
                        </div>
                        {errors.companyWebsite && (
                          <p className="flex items-center text-[#EE3535] text-[14px] mt-[4px] schibsted-grotesk">
                            {errors.companyWebsite}
                          </p>
                        )}
                      </div>

                      {/* Description */}
                      <div>
                        <label
                          htmlFor="description"
                          className="text-[#090D28] text-[14px] font-[500] leading-[100%] schibsted-grotesk mb-[5px]"
                        >
                          Tell us briefly why you&apos;re interested? <span style={{ color: "#EE3535" }}>*</span>
                        </label>
                        <div className="relative">
                          <textarea
                            className={`w-full h-[100px] px-[16px] py-2.5 rounded-[8px] border border-[#D1D7DD] bg-white text-[#090D28] schibsted-grotesk text-base font-normal leading-[120%] outline-none transition-colors duration-200 resize-none placeholder:text-[#6C6F87] placeholder:schibsted-grotesk placeholder:text-base placeholder:font-normal placeholder:leading-[100%] focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/10 ${errors.description ? "border-[#EE3535] focus:border-[#EE3535] focus:ring-[#EF4444]/10" : ""}`}
                            name="description"
                            id="description"
                            placeholder="Your interest"
                            value={formData.description}
                            onChange={handleChange}
                          />
                        </div>
                        {errors.description && (
                          <p className="flex items-center text-[#EE3535] text-[14px] mt-[4px] schibsted-grotesk">
                            {errors.description}
                          </p>
                        )}
                      </div>

                      {/* ReCAPTCHA */}
                      {/* <div className="recaptcha-container">
                        <ReCAPTCHA
                          sitekey={recaptchaSiteKey}
                          onChange={handleRecaptchaChange}
                          onExpired={() => {
                            setRecaptchaToken(null);
                            setFormData((prev) => ({ ...prev, "g-recaptcha-response": "" }));
                            notifyerror("reCAPTCHA expired. Please try again.");
                          }}
                        />
                        {errors.captcha && (
                          <p className="flex items-center text-[#EE3535] text-[14px] mt-[4px] schibsted-grotesk">
                            {errors.captcha}
                          </p>
                        )}
                      </div> */}

                      {/* Submit Button */}
                      <button
                        type="submit"
                        className={`w-full h-[42px] px-5 rounded-[22px] text-white schibsted-grotesk text-base font-normal flex items-center justify-center leading-[120%] disabled:opacity-60 disabled:cursor-not-allowed bg-[#195AFF] hover:bg-[#1347CC]`}
                        // disabled={isSubmitDisabled}
                      >
                        {formLoading ? (
                          <div className="flex items-center text-center">
                            <svg
                              className="animate-spin h-5 w-5 mr-2 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Requesting...
                          </div>
                        ) : (
                          "Continue"
                        )}
                      </button>

                      {/* Terms */}
                      <p className="text-[#6C6F87] m-0 mt-[16px] text-[14px] schibsted-grotesk text-center">
                        By clicking the button, I agree to Vilva&apos;s{" "}
                        <Link
                          href="/terms-and-condition"
                          className="text-[#195AFF] text-[13px] font-normal leading-[100%] underline schibsted-grotesk"
                        >
                          Terms of Services
                        </Link>{" "}
                        &amp;{" "}
                        <Link
                          href="/privacy-policy"
                          className="text-[#195AFF] text-[13px] font-normal leading-[100%] underline schibsted-grotesk"
                        >
                          Policies
                        </Link>
                      </p>
                    </form>
                  ) : (
                    <form onSubmit={handleOtpSubmit} className="space-y-[16px] px-1">
                      <div className="text-center mb-8">
                        <h2 className="text-[#002F81] text-2xl font-bold mb-2 font-NeueMontreal">
                          We Just Sent an OTP
                        </h2>
                        <p className="text-[#6C6F87] text-sm mb-4 schibsted-grotesk">
                          Just a quick check to confirm your request, enter the code sent to your Email / WhatsApp
                        </p>
                      </div>

                      <OtpChannelNotice channel={otpChannel} sentTo={otpSentTo} />

                      <label
                        htmlFor="otp"
                        className="text-[#090D28] schibsted-grotesk text-sm font-medium mb-1 flex items-center"
                      >
                        OTP <span className="text-[#EE3535] ml-1">*</span>
                      </label>

                      <div className="flex gap-[16px] justify-center">
                        {otpData.map((digit, index) => (
                          <div key={index} className="relative">
                            <input
                              ref={index === 0 ? otpInputRef : null}
                              id={`otp-${index}`}
                              type="text"
                              maxLength={1}
                              value={digit}
                              onChange={(e) => handleOtpChange(index, e.target.value)}
                              onKeyDown={(e) => handleOtpKeyDown(index, e)}
                              className={`h-[56px] w-[56px] flex justify-center items-center rounded-[8px] border border-[#D1D7DD] bg-white schibsted-grotesk text-base font-normal leading-[120%] outline-none transition-colors duration-200 placeholder:text-[#6C6F87] placeholder:schibsted-grotesk placeholder:text-base placeholder:font-normal placeholder:leading-[100%] focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/10 text-center ${errors.otp ? "border-[#EE3535] focus:border-[#EE3535] focus:ring-[#EF4444]/10" : ""}`}
                            />
                          </div>
                        ))}
                      </div>

                      {errors.otp && (
                        <p className="text-[#EE3535] text-sm mt-1 text-center">{errors.otp}</p>
                      )}

                      <button
                        type="submit"
                        disabled={formLoading}
                        ref={buttonRef}
                        className={`w-full h-[42px] px-5 rounded-[22px] text-white font-LatinkaSemiBold text-base font-normal flex items-center justify-center leading-[120%] disabled:opacity-60 disabled:cursor-not-allowed ${formLoading ? "bg-gray-400 cursor-not-allowed" : "bg-[#195AFF] hover:bg-[#1347CC]"}`}
                      >
                        {formLoading ? (
                          <div className="flex items-center text-center">
                            <svg
                              className="animate-spin h-5 w-5 mr-2 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Verifying...
                          </div>
                        ) : (
                          "Submit Request"
                        )}
                      </button>

                      <p className="text-[#6C6F87] text-sm text-center mt-2 schibsted-grotesk">
                        Didn&apos;t receive OTP?{" "}
                        <span
                          className={`font-Schibsted_Grotesk text-[12px] not-italic font-[400] leading-[150%] underline decoration-solid underline-offset-auto text-center ${canResendOtp && !formLoading ? "text-[#195AFF] underline cursor-pointer" : "text-[#8d8d8d] cursor-not-allowed"}`}
                          onClick={handleResendOtp}
                        >
                          Resend OTP {canResendOtp ? "" : `(${resendTimer} Secs)`}
                        </span>
                      </p>
                    </form>
                  )}
                </div>
              </div>

              {/* Right Side - Partner Info */}
              <div className="hidden md:block h-full py-[30px] px-[48px] pb-[24px] text-white login-bg">
                <PartnerRightSec />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="pt-10 z-0 bg-[#EBF0FF] sm:pt-16 lg:pt-24 overflow-hidden">
        <div className="container">
          <section>
            <p className="text-[#555F88] text-center font-Schibsted_Grotesk text-[12px] font-normal">
              © Copyright {new Date().getFullYear()} Vilva Business All Rights Reserved
            </p>
          </section>
        </div>
        <div className="px-2">
          <p className="text-[#DDE2F1] text-[30px] whitespace-nowrap md:text-[60px] lg:text-[100px] p-0 m-0 relative top-1 md:top-[16px] font-extrabold leading-none font-NeueMontreal text-center">
            MADE IN {location}
          </p>
        </div>
      </footer>
    </React.Fragment>
  );
};